import { useState } from "react";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { dt, DOC_TIPI, RITENUTE, CAUSALI_PAG } from "@/lib/api";
import { Field, Panel } from "@/components/Common";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const Sel = ({ value, onChange, options, testid }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger data-testid={testid}><SelectValue /></SelectTrigger>
    <SelectContent>{Object.entries(options).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
  </Select>
);

const EMPTY_DOC = { tipo: "ordine", id_documento: "", data: "", codice_commessa: "", cup: "", cig: "", linee: "" };
const DOC_FIELDS = [["id_documento", "Identificativo del documento", 20], ["data", "Data del documento", undefined, "date"],
  ["codice_commessa", "Codice Commessa/Convenzione", 100], ["cup", "Codice CUP", 15], ["cig", "Codice CIG", 15], ["linee", "Linea riferimento documento (es. 1,2)"]];

const DocDialog = ({ doc, onSave, onClose }) => {
  const [d, setD] = useState(doc);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>CIG/CUP fattura</DialogTitle></DialogHeader>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Documento" className="sm:col-span-2"><Sel testid="docrif-tipo" value={d.tipo} onChange={(v) => setD({ ...d, tipo: v })} options={DOC_TIPI} /></Field>
          {DOC_FIELDS.map(([k, label, max, type]) => (
            <Field key={k} label={label}><Input data-testid={`docrif-${k}`} type={type || "text"} maxLength={max} value={d[k] || ""} onChange={(e) => setD({ ...d, [k]: e.target.value })} /></Field>
          ))}
        </div>
        <DialogFooter><Button data-testid="docrif-save" disabled={!d.id_documento.trim()} onClick={() => onSave(d)}>Conferma</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const DocumentiPanel = ({ docs, setDocs }) => {
  const [edit, setEdit] = useState(null);
  const save = (d) => {
    setDocs(edit.i === -1 ? [...docs, d] : docs.map((x, j) => (j === edit.i ? d : x)));
    setEdit(null);
  };
  return (
    <Panel title="CIG/CUP e documenti di riferimento" action={<Button size="sm" variant="outline" data-testid="add-docrif-button" onClick={() => setEdit({ i: -1, d: { ...EMPTY_DOC } })}><Plus className="mr-1 h-4 w-4" />Aggiungi</Button>}>
      {docs.length === 0 ? <p className="text-sm text-slate-500">Ordine di acquisto, contratto, convenzione, ricezione o fattura collegata, con CIG e CUP (obbligatori verso la PA).</p> : (
        <div className="space-y-2">
          {docs.map((d, i) => (
            <div key={i} data-testid={`docrif-row-${i}`} className="flex flex-wrap items-center gap-x-4 gap-y-1 rounded-md border border-slate-200 px-3 py-2 text-sm">
              <b>{DOC_TIPI[d.tipo]}</b><span className="font-mono">n. {d.id_documento}</span>{d.data && <span>{dt(d.data)}</span>}
              {d.cig && <span className="font-mono text-xs">CIG {d.cig}</span>}{d.cup && <span className="font-mono text-xs">CUP {d.cup}</span>}
              <span className="ml-auto flex">
                <Button variant="ghost" size="icon" data-testid={`docrif-edit-${i}`} onClick={() => setEdit({ i, d })}><Pencil className="h-4 w-4" /></Button>
                <Button variant="ghost" size="icon" data-testid={`docrif-remove-${i}`} onClick={() => setDocs(docs.filter((_, j) => j !== i))}><Trash2 className="h-4 w-4" /></Button>
              </span>
            </div>
          ))}
        </div>
      )}
      {edit && <DocDialog doc={edit.d} onSave={save} onClose={() => setEdit(null)} />}
    </Panel>
  );
};

const RitRow = ({ r, i, onChange, onRemove, accLocked }) => (
  <div data-testid={`ritenuta-row-${i}`} className="space-y-2 rounded-md border border-slate-200 p-3">
    <div className="flex gap-2">
      <div className="flex-1"><Sel testid={`ritenuta-tipo-${i}`} value={r.tipo} onChange={(v) => onChange({ ...r, tipo: v })}
        options={Object.fromEntries(Object.entries(RITENUTE).filter(([k]) => !accLocked || !["RT01", "RT02"].includes(k) || k === r.tipo))} /></div>
      <Button variant="ghost" size="icon" data-testid={`ritenuta-remove-${i}`} onClick={onRemove} className="text-slate-400 hover:text-rose-600"><Trash2 className="h-4 w-4" /></Button>
    </div>
    <div className="grid grid-cols-2 gap-2">
      <Field label="Aliquota %"><Input data-testid={`ritenuta-aliquota-${i}`} type="number" step="0.01" value={r.aliquota} onChange={(e) => onChange({ ...r, aliquota: e.target.value })} /></Field>
      <Field label="% imponibile"><Input data-testid={`ritenuta-perc-${i}`} type="number" step="0.01" value={r.perc_imponibile} onChange={(e) => onChange({ ...r, perc_imponibile: e.target.value })} /></Field>
    </div>
    <Sel testid={`ritenuta-causale-${i}`} value={r.causale} onChange={(v) => onChange({ ...r, causale: v })} options={CAUSALI_PAG} />
  </div>
);

export const RitenuteFields = ({ list, setList, forfettario }) => {
  const hasAcc = list.some((r) => ["RT01", "RT02"].includes(r.tipo));
  return (
    <div className="space-y-3">
      {list.map((r, i) => <RitRow key={i} r={r} i={i} accLocked={forfettario || list.some((x, j) => j !== i && ["RT01", "RT02"].includes(x.tipo))} onChange={(x) => setList(list.map((y, j) => (j === i ? x : y)))} onRemove={() => setList(list.filter((_, j) => j !== i))} />)}
      <div className="flex flex-wrap gap-2">
        {!forfettario && !hasAcc && <Button size="sm" variant="outline" data-testid="add-ritenuta-acconto" onClick={() => setList([...list, { tipo: "RT01", aliquota: 20, perc_imponibile: 100, causale: "A" }])}><Plus className="mr-1 h-4 w-4" />Ritenuta d'acconto</Button>}
        <Button size="sm" variant="outline" data-testid="add-ritenuta-contributo" onClick={() => setList([...list, { tipo: "RT04", aliquota: 8.5, perc_imponibile: 100, causale: "R" }])}><Plus className="mr-1 h-4 w-4" />Contributo (ENASARCO/INPS)</Button>
      </div>
      <p className="text-xs text-slate-500">I contributi non sono soggetti a IVA: vengono trattenuti dal netto a pagare. Agenti: ritenuta d'acconto 23% sul 50% + ENASARCO.{forfettario && " In regime forfettario la ritenuta d'acconto non si applica."}</p>
    </div>
  );
};
